import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Target, 
  Plus, 
  Edit, 
  Trash2, 
  Check,
  X,
  AlertCircle,
  TrendingUp,
  Award,
  Settings,
  BarChart3,
  Zap,
  Star
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCORING_CATEGORIES = [
  { value: 'source_quality', label: 'Source Quality', icon: '🎯', color: 'bg-blue-100 text-blue-800' },
  { value: 'service_type', label: 'Service Type', icon: '📋', color: 'bg-purple-100 text-purple-800' },
  { value: 'response_speed', label: 'Response Speed', icon: '⚡', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'budget_indicated', label: 'Budget Indicated', icon: '💰', color: 'bg-green-100 text-green-800' },
  { value: 'contact_completeness', label: 'Contact Completeness', icon: '📞', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'engagement_level', label: 'Engagement Level', icon: '🔥', color: 'bg-red-100 text-red-800' },
  { value: 'custom', label: 'Custom', icon: '⚙️', color: 'bg-gray-100 text-gray-800' },
];

const ScoringRulesManager = () => {
  const { getAuthHeader, admin } = useAuth();
  const [scoringRules, setScoringRules] = useState([]);
  const [activeRule, setActiveRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [isAddParameterOpen, setIsAddParameterOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    max_score: 100,
    parameters: [],
    is_active: false
  });

  const [parameterForm, setParameterForm] = useState({
    category: 'source_quality',
    name: '',
    description: '',
    points: 10
  });

  useEffect(() => {
    fetchScoringRules();
    fetchActiveRule();
  }, []);

  const fetchScoringRules = async () => {
    try {
      const response = await axios.get(`${API}/admin/scoring-rules`, {
        headers: getAuthHeader()
      });
      setScoringRules(response.data);
    } catch (error) {
      console.error('Error fetching scoring rules:', error);
      alert('Failed to fetch scoring rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveRule = async () => {
    try {
      const response = await axios.get(`${API}/admin/scoring-rules/active/current`, {
        headers: getAuthHeader()
      });
      setActiveRule(response.data);
    } catch (error) {
      console.error('Error fetching active rule:', error);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/scoring-rules`, formData, {
        headers: getAuthHeader()
      });
      setIsCreateDialogOpen(false);
      resetForm();
      fetchScoringRules();
      fetchActiveRule();
      alert('Scoring rule created successfully!');
    } catch (error) {
      console.error('Error creating scoring rule:', error);
      alert('Failed to create scoring rule');
    }
  };

  const handleUpdateRule = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/scoring-rules/${editingRule.id}`, formData, {
        headers: getAuthHeader()
      });
      setIsEditDialogOpen(false);
      setEditingRule(null);
      resetForm();
      fetchScoringRules();
      fetchActiveRule();
      alert('Scoring rule updated successfully!');
    } catch (error) {
      console.error('Error updating scoring rule:', error);
      alert('Failed to update scoring rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this scoring rule?');
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API}/admin/scoring-rules/${ruleId}`, {
        headers: getAuthHeader()
      });
      fetchScoringRules();
      alert('Scoring rule deleted successfully!');
    } catch (error) {
      console.error('Error deleting scoring rule:', error);
      alert('Failed to delete scoring rule');
    }
  };

  const handleActivateRule = async (ruleId) => {
    try {
      await axios.post(`${API}/admin/scoring-rules/${ruleId}/activate`, {}, {
        headers: getAuthHeader()
      });
      fetchScoringRules();
      fetchActiveRule();
      alert('Scoring rule activated successfully!');
    } catch (error) {
      console.error('Error activating rule:', error);
      alert('Failed to activate rule');
    }
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      max_score: rule.max_score,
      parameters: rule.parameters || [],
      is_active: rule.is_active
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      max_score: 100,
      parameters: [],
      is_active: false
    });
  };

  const handleAddParameter = () => {
    const newParameter = {
      ...parameterForm,
      id: Date.now().toString()
    };
    setFormData({
      ...formData,
      parameters: [...formData.parameters, newParameter]
    });
    setParameterForm({
      category: 'source_quality',
      name: '',
      description: '',
      points: 10
    });
    setIsAddParameterOpen(false);
  };

  const handleRemoveParameter = (paramId) => {
    setFormData({
      ...formData,
      parameters: formData.parameters.filter(p => p.id !== paramId)
    });
  };

  const calculateTotalPoints = (parameters) => {
    return parameters.reduce((sum, p) => sum + (p.points || 0), 0);
  };

  const getCategoryInfo = (category) => {
    return SCORING_CATEGORIES.find(c => c.value === category) || SCORING_CATEGORIES[0];
  };

  if (loading) {
    return (
      <div className="scoring-rules-manager">
        <div className="scoring-header">
          <h1><Target size={24} /> Lead Scoring Rules</h1>
        </div>
        <div className="text-center py-8">Loading scoring rules...</div>
      </div>
    );
  }

  return (
    <div className="scoring-rules-manager">
      <div className="scoring-header">
        <div>
          <h1>
            <Target size={28} /> Lead Scoring Rules
          </h1>
          <p className="text-muted-foreground">Configure scoring parameters to prioritize high-quality leads</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="scoring-btn-create">
              <Plus size={20} /> Create Scoring Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="scoring-dialog">
            <DialogHeader>
              <DialogTitle>Create New Scoring Rule</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRule}>
              <div className="space-y-4">
                <div>
                  <Label>Rule Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., High-Value Lead Scoring"
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this scoring rule"
                  />
                </div>
                <div>
                  <Label>Max Score</Label>
                  <Input
                    type="number"
                    value={formData.max_score}
                    onChange={(e) => setFormData({ ...formData, max_score: parseInt(e.target.value) })}
                    min="50"
                    max="1000"
                  />
                </div>

                {/* Parameters Section */}
                <div className="scoring-parameters-section">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base">Scoring Parameters ({formData.parameters.length})</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddParameterOpen(true)}
                    >
                      <Plus size={16} /> Add Parameter
                    </Button>
                  </div>
                  
                  {formData.parameters.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed rounded-lg">
                      <Award size={32} className="mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-muted-foreground">No parameters added yet</p>
                      <p className="text-xs text-muted-foreground">Click "Add Parameter" to define scoring criteria</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {formData.parameters.map((param) => {
                        const categoryInfo = getCategoryInfo(param.category);
                        return (
                          <div key={param.id} className="scoring-parameter-item">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xl">{categoryInfo.icon}</span>
                                  <span className={`scoring-category-badge ${categoryInfo.color}`}>
                                    {categoryInfo.label}
                                  </span>
                                  <span className="scoring-points-badge">
                                    +{param.points} pts
                                  </span>
                                </div>
                                <p className="font-medium text-sm">{param.name}</p>
                                {param.description && (
                                  <p className="text-xs text-muted-foreground">{param.description}</p>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveParameter(param.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {formData.parameters.length > 0 && (
                    <div className="scoring-total-summary">
                      <BarChart3 size={18} />
                      <span>Total Possible Score:</span>
                      <strong>{calculateTotalPoints(formData.parameters)} points</strong>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is-active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <Label htmlFor="is-active" className="cursor-pointer">
                    <Zap size={16} className="inline" /> Set as Active Rule
                  </Label>
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Rule</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Rule Card */}
      {activeRule && activeRule.id && (
        <Card className="scoring-active-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="scoring-active-badge">
                  <Zap size={20} /> ACTIVE
                </div>
                <div>
                  <CardTitle>{activeRule.name}</CardTitle>
                  <CardDescription>{activeRule.description}</CardDescription>
                </div>
              </div>
              <div className="scoring-stat-badge">
                <TrendingUp size={18} />
                <span>Max: {activeRule.max_score} pts</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="scoring-parameters-grid">
              {activeRule.parameters && activeRule.parameters.length > 0 ? (
                activeRule.parameters.map((param) => {
                  const categoryInfo = getCategoryInfo(param.category);
                  return (
                    <div key={param.id} className="scoring-active-parameter">
                      <span className="text-2xl mb-2">{categoryInfo.icon}</span>
                      <div className="scoring-param-points">+{param.points}</div>
                      <p className="font-medium text-sm">{param.name}</p>
                      <span className={`scoring-category-badge ${categoryInfo.color} mt-1`}>
                        {categoryInfo.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="col-span-full text-center text-muted-foreground">No parameters configured</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Rules List */}
      <div className="scoring-rules-grid">
        {scoringRules.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Target size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No Scoring Rules Created</h3>
              <p className="text-muted-foreground mb-4">Create your first scoring rule to start prioritizing leads</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus size={20} /> Create First Rule
              </Button>
            </CardContent>
          </Card>
        ) : (
          scoringRules.map((rule) => (
            <Card key={rule.id} className={`scoring-rule-card ${rule.is_active ? 'active' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{rule.name}</CardTitle>
                      {rule.is_active && (
                        <span className="scoring-active-pill">
                          <Star size={12} /> Active
                        </span>
                      )}
                    </div>
                    <CardDescription>{rule.description || 'No description provided'}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="scoring-rule-stats">
                  <div className="scoring-stat-item">
                    <Award size={20} className="text-primary" />
                    <div>
                      <div className="text-2xl font-bold">{rule.parameters?.length || 0}</div>
                      <div className="text-xs text-muted-foreground">Parameters</div>
                    </div>
                  </div>
                  <div className="scoring-stat-item">
                    <TrendingUp size={20} className="text-green-600" />
                    <div>
                      <div className="text-2xl font-bold">{calculateTotalPoints(rule.parameters || [])}</div>
                      <div className="text-xs text-muted-foreground">Total Points</div>
                    </div>
                  </div>
                  <div className="scoring-stat-item">
                    <BarChart3 size={20} className="text-blue-600" />
                    <div>
                      <div className="text-2xl font-bold">{rule.max_score}</div>
                      <div className="text-xs text-muted-foreground">Max Score</div>
                    </div>
                  </div>
                </div>

                <div className="scoring-rule-actions">
                  {!rule.is_active && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleActivateRule(rule.id)}
                      className="flex-1"
                    >
                      <Zap size={16} /> Activate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(rule)}
                  >
                    <Edit size={16} /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="scoring-dialog">
          <DialogHeader>
            <DialogTitle>Edit Scoring Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRule}>
            <div className="space-y-4">
              <div>
                <Label>Rule Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Max Score</Label>
                <Input
                  type="number"
                  value={formData.max_score}
                  onChange={(e) => setFormData({ ...formData, max_score: parseInt(e.target.value) })}
                  min="50"
                  max="1000"
                />
              </div>

              {/* Parameters Section */}
              <div className="scoring-parameters-section">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Scoring Parameters ({formData.parameters.length})</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAddParameterOpen(true)}
                  >
                    <Plus size={16} /> Add Parameter
                  </Button>
                </div>
                
                {formData.parameters.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed rounded-lg">
                    <Award size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-muted-foreground">No parameters added yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {formData.parameters.map((param) => {
                      const categoryInfo = getCategoryInfo(param.category);
                      return (
                        <div key={param.id} className="scoring-parameter-item">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xl">{categoryInfo.icon}</span>
                                <span className={`scoring-category-badge ${categoryInfo.color}`}>
                                  {categoryInfo.label}
                                </span>
                                <span className="scoring-points-badge">
                                  +{param.points} pts
                                </span>
                              </div>
                              <p className="font-medium text-sm">{param.name}</p>
                              {param.description && (
                                <p className="text-xs text-muted-foreground">{param.description}</p>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveParameter(param.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {formData.parameters.length > 0 && (
                  <div className="scoring-total-summary">
                    <BarChart3 size={18} />
                    <span>Total Possible Score:</span>
                    <strong>{calculateTotalPoints(formData.parameters)} points</strong>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-active-edit"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <Label htmlFor="is-active-edit" className="cursor-pointer">
                  <Zap size={16} className="inline" /> Set as Active Rule
                </Label>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Update Rule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Parameter Dialog */}
      <Dialog open={isAddParameterOpen} onOpenChange={setIsAddParameterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Scoring Parameter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select
                value={parameterForm.category}
                onValueChange={(value) => setParameterForm({ ...parameterForm, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCORING_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parameter Name *</Label>
              <Input
                value={parameterForm.name}
                onChange={(e) => setParameterForm({ ...parameterForm, name: e.target.value })}
                placeholder="e.g., Website Form Submission"
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={parameterForm.description}
                onChange={(e) => setParameterForm({ ...parameterForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div>
              <Label>Points *</Label>
              <Input
                type="number"
                value={parameterForm.points}
                onChange={(e) => setParameterForm({ ...parameterForm, points: parseInt(e.target.value) })}
                min="1"
                max="100"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddParameterOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddParameter} disabled={!parameterForm.name}>
              <Plus size={16} /> Add Parameter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScoringRulesManager;
